/**
 * V2AnimationSection — dedicated editor section for per-screen motion settings
 *
 * Single home for animation controls, available on ALL 13 StepKinds (Carousel /
 * ProgressSteps / FlexibleLayout previously had no entrance controls at all).
 * Moved out of Basic Config: Screen Entrance (content.screenAnimation) and the
 * legacy Animation Enabled toggle (content.animated).
 *
 * UX (approved mock: .design/screen-edit-animation-section/):
 * - "Inherited from theme" hint when the screen has no own screenAnimation but
 *   the FlowDocument carries defaultScreenAnimation
 * - Screen Entrance nullable-checkbox group (preset / duration / stagger)
 * - Legacy toggle renders ONLY when the screen already had animated:true at
 *   dialog-open time and no screenAnimation override exists — clean screens
 *   never see it, and it never vanishes mid-edit when switched off
 * - Advanced (easing + per-element overrides) lives in a bordered Collapse —
 *   NEVER a nested SAccordion (the nesting guard throws)
 * - Contextual help: the section-header "?" comes from SAccordion via the
 *   editor's GuideAnchorProvider (`section-animation`); each hand-built control
 *   carries its FieldConfig guideKey through AnimationFieldGuide
 *
 * Runtime priority: screenAnimation > animated:true (fadeIn) > theme default.
 */

import React, { useCallback, useMemo, useState } from 'react';
import { Box, Checkbox, Typography, FormControl, Select, MenuItem, Collapse } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { SAccordion, StyledNumberInput, WithGuideHelp } from '@modules/ui-components';
import type { FlowDocument, ElementAnimationTarget } from '@segmently/schema-models/v2';
import { ToggleElement } from '../../../factories/elements/ToggleElement';
import { EditorGuideProvider, useEditorGuideService } from '../../../contexts';
import {
  V2_SCREEN_ANIMATION_CONFIG,
  V2_SCREEN_ANIMATION_PRESET_CONFIG,
  V2_SCREEN_ANIMATION_DURATION_CONFIG,
  V2_SCREEN_ANIMATION_STAGGER_CONFIG,
  V2_ANIMATED_CONFIG,
  V2_ANIMATION_EASING_CONFIG,
  V2_ANIMATION_EASING_OPTIONS,
  V2_ANIMATION_SLOT_CONFIGS,
  V2_ELEMENT_ANIMATIONS_PATH,
} from '../../../config/v2AnimationSectionConfig';
import { V2_ENTRANCE_PRESET_OPTIONS } from '../../../config/v2AnimationPresetOptions';
import { withScreenEditorFieldGuide } from '../../../config/screenEditorFieldGuideResolver';
import { SECTION_MARGIN_BOTTOM, FIELD_MARGIN_BOTTOM } from '../../../config/ui';
import {
  checkboxIconSx,
  checkboxCheckedIconSx,
  checkboxSx,
  mainFieldLabelEnabledSx,
  mainFieldLabelDisabledSx,
} from '../../../config/editorFieldStyles';
import { getValueByPath } from '../../../utils/v2FieldOperations';
import { getSectionTestId, getFieldTestId, getCheckboxTestId, getInputTestId } from '../../../utils/testIdUtils';
import type { FlowStepNode } from '@modules/reactFlow/adapters/V2ScreenAdapter';

/**
 * Per-slot Select sentinel for "None" — persisted as
 * `{ elementRef, animation: { disabled: true } }`, which suppresses this slot
 * while the rest of the Screen Entrance distribution still plays.
 */
const SLOT_NONE_VALUE = '__none__';

export interface V2AnimationSectionProps {
  /** V2 StepNode being edited */
  step: FlowStepNode;

  /** Current editing locale */
  locale: string;

  /** Callback when a V2 path value changes */
  onChange: (path: string, value: unknown) => void;

  /** Whether section is disabled (saving state) */
  disabled?: boolean;

  /** Whether section is expanded by default */
  defaultExpanded?: boolean;

  /** FlowDocument — source of the theme-level defaultScreenAnimation for the inheritance hint */
  flowDocument?: FlowDocument | null;

  /**
   * Fired after any animation setting changes — the editor dialog uses it to
   * auto-replay the preview so the user immediately sees the new motion.
   */
  onAnimationSettingsChange?: () => void;
}

/** Preset value → human label, falling back to the raw value */
function presetLabel(preset: string | undefined): string | undefined {
  if (!preset) return undefined;
  return V2_ENTRANCE_PRESET_OPTIONS.find((o) => o.value === preset)?.label ?? preset;
}

interface AnimationFieldGuideProps {
  /** `FieldConfig.guideKey` of the control being explained */
  guideKey?: string;
  /** Block-level row (full-width select, block caption) instead of an inline label */
  block?: boolean;
  children: React.ReactNode;
}

/**
 * Contextual-help "?" for the section's hand-built controls (the
 * `/_shared:add-guide` pattern). These are raw MUI controls, not factory
 * elements, so they do not get the automatic FieldConfig guide wrap. Reads the
 * nearest EditorGuideProvider — the same service ToggleElement uses for the
 * legacy toggle — and emits the registry testid `guide-anchor-<guideKey>`.
 * Without a guide service or key it renders the bare control.
 */
const AnimationFieldGuide: React.FC<AnimationFieldGuideProps> = ({ guideKey, block = false, children }) => {
  const guideService = useEditorGuideService();
  if (!guideKey || !guideService) return <>{children}</>;
  return (
    <WithGuideHelp
      guideKey={guideKey}
      guideService={guideService}
      data-testid={`guide-anchor-${guideKey}`}
      position="inline-end"
      iconSize="small"
      sx={block ? { display: 'flex' } : undefined}
    >
      {children}
    </WithGuideHelp>
  );
};

export const V2AnimationSection: React.FC<V2AnimationSectionProps> = ({
  step,
  locale: _locale,
  onChange,
  disabled = false,
  defaultExpanded = false,
  flowDocument,
  onAnimationSettingsChange,
}) => {
  // ============================================================================
  // VALUE GETTERS
  // ============================================================================

  // Screen entrance animation (nullable object, schema 2.17.0)
  const screenAnimation = useMemo(() => {
    return getValueByPath<{
      enterAnimation?: { preset?: string; timing?: { duration?: number } };
      stagger?: { delayPerItem?: number };
    }>(step, V2_SCREEN_ANIMATION_CONFIG.path);
  }, [step]);

  const isScreenAnimationEnabled = screenAnimation !== null && screenAnimation !== undefined;
  const screenAnimationPreset = screenAnimation?.enterAnimation?.preset
    ?? (V2_SCREEN_ANIMATION_PRESET_CONFIG.defaultValue as string);
  const screenAnimationDuration = screenAnimation?.enterAnimation?.timing?.duration
    ?? (V2_SCREEN_ANIMATION_DURATION_CONFIG.defaultValue as number);
  const screenAnimationStagger = screenAnimation?.stagger?.delayPerItem
    ?? (V2_SCREEN_ANIMATION_STAGGER_CONFIG.defaultValue as number);

  const animated = useMemo(() => {
    const value = getValueByPath<boolean>(step, V2_ANIMATED_CONFIG.path);
    return value ?? false;
  }, [step]);

  // Legacy toggle visibility is fixed at mount: shown only for screens that
  // ALREADY carried animated:true when the dialog opened. Keying off the live
  // value would make the control vanish the moment the user switches it off.
  const [legacyToggleMounted] = useState(() => animated === true);
  const showLegacyToggle = legacyToggleMounted && !isScreenAnimationEnabled;

  // Theme inheritance hint: no own setting (and no legacy animated fadeIn),
  // but the funnel's theme provides a default entrance.
  const themeDefaultEnter = flowDocument?.defaultScreenAnimation?.enterAnimation;
  const showInheritedHint = !isScreenAnimationEnabled && !animated && !!themeDefaultEnter?.preset;
  const inheritedLabel = presetLabel(themeDefaultEnter?.preset);
  const inheritedDuration = themeDefaultEnter?.timing?.duration;

  // ── Advanced: easing + per-element overrides ──────────────────────────────
  const [advancedOpen, setAdvancedOpen] = useState(false);

  const screenAnimationEasing = getValueByPath<string>(
    step, V2_ANIMATION_EASING_CONFIG.path,
  ) ?? '';

  // Per-element overrides live in content.elementAnimations[] (highest-priority
  // resolver layer, exact-ref mount lookup — see v2AnimationSectionConfig).
  const elementAnimations = useMemo(() => {
    return getValueByPath<ElementAnimationTarget[]>(step, V2_ELEMENT_ANIMATIONS_PATH) ?? [];
  }, [step]);

  // Only slots whose content block exists on this step get a row (mirrors the
  // runtime's SCREEN_SLOTS presence checks).
  const presentSlots = useMemo(() => {
    return V2_ANIMATION_SLOT_CONFIGS.filter((slot) =>
      slot.presencePaths.some((p) => getValueByPath(step, p) != null),
    );
  }, [step]);

  const getSlotPreset = useCallback(
    (slotRefs: string[]): string => {
      const entry = elementAnimations.find((t) => t?.elementRef === slotRefs[0]);
      if (entry?.animation?.disabled === true) return SLOT_NONE_VALUE;
      return entry?.animation?.preset ?? '';
    },
    [elementAnimations],
  );

  // ============================================================================
  // HANDLERS
  // ============================================================================

  const notifySettingsChange = useCallback(() => {
    onAnimationSettingsChange?.();
  }, [onAnimationSettingsChange]);

  // Screen entrance enable/disable (nullable-object pattern, like Countdown)
  const handleScreenAnimationEnabledChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.checked) {
        onChange(V2_SCREEN_ANIMATION_CONFIG.path, {
          enterAnimation: {
            preset: V2_SCREEN_ANIMATION_PRESET_CONFIG.defaultValue,
            timing: { duration: V2_SCREEN_ANIMATION_DURATION_CONFIG.defaultValue },
          },
          stagger: { delayPerItem: V2_SCREEN_ANIMATION_STAGGER_CONFIG.defaultValue },
        });
      } else {
        onChange(V2_SCREEN_ANIMATION_CONFIG.path, null);
      }
      notifySettingsChange();
    },
    [onChange, notifySettingsChange]
  );

  const handleScreenAnimationPresetChange = useCallback(
    (event: { target: { value: string } }) => {
      onChange(V2_SCREEN_ANIMATION_PRESET_CONFIG.path, event.target.value);
      notifySettingsChange();
    },
    [onChange, notifySettingsChange]
  );

  const handleScreenAnimationDurationChange = useCallback(
    (value: number | null) => {
      if (value !== null) {
        onChange(V2_SCREEN_ANIMATION_DURATION_CONFIG.path, value);
        notifySettingsChange();
      }
    },
    [onChange, notifySettingsChange]
  );

  const handleScreenAnimationStaggerChange = useCallback(
    (value: number | null) => {
      if (value !== null) {
        onChange(V2_SCREEN_ANIMATION_STAGGER_CONFIG.path, value);
        notifySettingsChange();
      }
    },
    [onChange, notifySettingsChange]
  );

  // Legacy animated toggle
  const handleAnimatedChange = useCallback(
    (value: boolean) => {
      onChange(V2_ANIMATED_CONFIG.path, value);
      notifySettingsChange();
    },
    [onChange, notifySettingsChange]
  );

  // Advanced: entrance easing ('' = Default → null clears the key)
  const handleEasingChange = useCallback(
    (event: { target: { value: string } }) => {
      onChange(V2_ANIMATION_EASING_CONFIG.path, event.target.value || null);
      notifySettingsChange();
    },
    [onChange, notifySettingsChange]
  );

  // Advanced: per-slot override — rewrites the row's refs in elementAnimations[].
  // Entries owned by OTHER refs (custom dot-path targets) are preserved.
  // '' = Inherit (remove refs → slot falls back to the Screen Entrance
  // distribution); SLOT_NONE_VALUE = disable this slot while others play
  // (writes ElementAnimation.disabled — the spec builder resolves it to null).
  const handleSlotPresetChange = useCallback(
    (slotRefs: string[], preset: string) => {
      const rest = elementAnimations.filter(
        (t) => !slotRefs.includes(t?.elementRef ?? ''),
      );
      const next = preset
        ? [
          ...rest,
          ...slotRefs.map((ref) => ({
            elementRef: ref,
            animation: preset === SLOT_NONE_VALUE ? { disabled: true } : { preset },
          }) as ElementAnimationTarget),
        ]
        : rest;
      onChange(V2_ELEMENT_ANIMATIONS_PATH, next.length > 0 ? next : null);
      notifySettingsChange();
    },
    [elementAnimations, onChange, notifySettingsChange]
  );

  // ============================================================================
  // RENDER
  // ============================================================================

  const SECTION_NAME = 'Animation';

  return (
    <SAccordion
      title="Animation"
      collapsible
      defaultExpanded={defaultExpanded}
      sx={{ marginBottom: SECTION_MARGIN_BOTTOM }}
      data-testid={getSectionTestId(SECTION_NAME)}
    >
      <EditorGuideProvider>
        {/* Inherited-from-theme hint */}
        {showInheritedHint && (
          <Box
            data-testid="animation-inherited-hint"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              bgcolor: 'action.hover',
              borderRadius: 1,
              px: 1.25,
              py: 1,
              mb: FIELD_MARGIN_BOTTOM,
            }}
          >
            <InfoOutlinedIcon sx={{ fontSize: 16, color: 'info.main' }} />
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Inherited from theme:{' '}
              <Box component="span" sx={{ color: 'text.primary', fontWeight: 500 }}>
                {inheritedLabel}
                {inheritedDuration ? ` · ${inheritedDuration} ms` : ''}
              </Box>
              . Enable Screen Entrance to override.
            </Typography>
          </Box>
        )}

        {/* Screen Entrance (nullable with checkbox, schema 2.17.0) */}
        <Box
          data-testid={getFieldTestId(SECTION_NAME, 'Screen Entrance')}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            mb: FIELD_MARGIN_BOTTOM,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Checkbox
              checked={isScreenAnimationEnabled}
              onChange={handleScreenAnimationEnabledChange}
              disabled={disabled}
              icon={<Box sx={checkboxIconSx} />}
              checkedIcon={<Box sx={checkboxCheckedIconSx}>✓</Box>}
              sx={checkboxSx}
              data-testid={getCheckboxTestId(SECTION_NAME, 'Screen Entrance')}
            />
            <AnimationFieldGuide guideKey={V2_SCREEN_ANIMATION_CONFIG.guideKey}>
              <Typography sx={isScreenAnimationEnabled ? mainFieldLabelEnabledSx : mainFieldLabelDisabledSx}>
                {V2_SCREEN_ANIMATION_CONFIG.label}
              </Typography>
            </AnimationFieldGuide>
          </Box>
          {isScreenAnimationEnabled && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, pl: 4, pt: 0.5 }}>
              <AnimationFieldGuide guideKey={V2_SCREEN_ANIMATION_PRESET_CONFIG.guideKey} block>
                <FormControl size="small" fullWidth>
                  <Select
                    value={screenAnimationPreset}
                    onChange={handleScreenAnimationPresetChange}
                    disabled={disabled}
                    data-testid={getInputTestId(SECTION_NAME, 'Screen Entrance Preset')}
                  >
                    {V2_ENTRANCE_PRESET_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </AnimationFieldGuide>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <AnimationFieldGuide guideKey={V2_SCREEN_ANIMATION_DURATION_CONFIG.guideKey}>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>Duration (ms)</Typography>
                </AnimationFieldGuide>
                <StyledNumberInput
                  value={screenAnimationDuration}
                  onChange={handleScreenAnimationDurationChange}
                  min={V2_SCREEN_ANIMATION_DURATION_CONFIG.min}
                  max={V2_SCREEN_ANIMATION_DURATION_CONFIG.max}
                  disabled={disabled}
                  data-testid={getInputTestId(SECTION_NAME, 'Screen Entrance Duration')}
                />
                <AnimationFieldGuide guideKey={V2_SCREEN_ANIMATION_STAGGER_CONFIG.guideKey}>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>Stagger (ms)</Typography>
                </AnimationFieldGuide>
                <StyledNumberInput
                  value={screenAnimationStagger}
                  onChange={handleScreenAnimationStaggerChange}
                  min={V2_SCREEN_ANIMATION_STAGGER_CONFIG.min}
                  max={V2_SCREEN_ANIMATION_STAGGER_CONFIG.max}
                  disabled={disabled}
                  data-testid={getInputTestId(SECTION_NAME, 'Screen Entrance Stagger')}
                />
              </Box>
            </Box>
          )}
        </Box>

        {/* Advanced — bordered Collapse (nested SAccordion is forbidden: nesting guard throws) */}
        <Box
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            mb: FIELD_MARGIN_BOTTOM,
          }}
        >
          <Box
            data-testid="animation-advanced-toggle"
            onClick={() => setAdvancedOpen((open) => !open)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 1.5,
              py: 1,
              cursor: 'pointer',
              userSelect: 'none',
            }}
          >
            <Typography variant="caption" sx={{ fontWeight: 500, color: 'text.secondary' }}>
              Advanced
            </Typography>
            <ExpandMoreIcon
              sx={{
                fontSize: 18,
                color: 'text.secondary',
                transform: advancedOpen ? 'rotate(180deg)' : 'none',
                transition: 'transform 150ms',
              }}
            />
          </Box>
          <Collapse in={advancedOpen}>
            <Box
              data-testid="animation-advanced"
              sx={{ px: 1.5, pb: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}
            >
              {/* Easing — only meaningful for an enabled Screen Entrance */}
              {isScreenAnimationEnabled && (
                <Box>
                  <AnimationFieldGuide guideKey={V2_ANIMATION_EASING_CONFIG.guideKey} block>
                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
                      Easing
                    </Typography>
                  </AnimationFieldGuide>
                  <FormControl size="small" fullWidth>
                    <Select
                      value={screenAnimationEasing}
                      onChange={handleEasingChange}
                      disabled={disabled}
                      displayEmpty
                      data-testid="input-animation-easing"
                    >
                      <MenuItem value="">Default (Ease Out)</MenuItem>
                      {V2_ANIMATION_EASING_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              )}

              {/* Per-element entrance overrides (elementAnimations[]) */}
              {presentSlots.length > 0 && (
                <Box>
                  <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
                    Per-element entrance
                  </Typography>
                  {presentSlots.map((slot) => (
                    <Box
                      key={slot.key}
                      sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}
                    >
                      <Typography variant="body2" sx={{ flex: 1, fontSize: 13 }}>
                        {slot.label}
                      </Typography>
                      <FormControl size="small" sx={{ width: 170 }}>
                        <Select
                          value={getSlotPreset(slot.elementRefs)}
                          onChange={(event) =>
                            handleSlotPresetChange(slot.elementRefs, event.target.value)}
                          disabled={disabled}
                          displayEmpty
                          data-testid={`input-animation-slot-${slot.key}`}
                        >
                          <MenuItem value="">Inherit</MenuItem>
                          <MenuItem value={SLOT_NONE_VALUE}>None</MenuItem>
                          {V2_ENTRANCE_PRESET_OPTIONS.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          </Collapse>
        </Box>

        {/* Legacy Animation Enabled toggle — legacy-data screens only */}
        {showLegacyToggle && (
          <Box sx={{ borderTop: '1px dashed', borderColor: 'divider', pt: 1 }}>
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                fontSize: '10px',
              }}
            >
              Legacy
            </Typography>
            <ToggleElement
              fieldConfig={withScreenEditorFieldGuide(V2_ANIMATED_CONFIG, 'animation')}
              value={animated}
              onChange={handleAnimatedChange}
              disabled={disabled}
              data-testid={getFieldTestId(SECTION_NAME, V2_ANIMATED_CONFIG.label)}
            />
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', pl: 0.5 }}>
              Plays the default fade-in. Configure Screen Entrance above to replace it.
            </Typography>
          </Box>
        )}
      </EditorGuideProvider>
    </SAccordion>
  );
};
